import { LoadingImageComponent } from "@/components/LoadingImage/LoadingImage.component";
import { devLogger } from "@/utils/dev-logger.util";
import { renderArticleSkeleton } from "./ArticleSkeleton";
const getConfiguration = (element, props) => {
    const getAttr = (name, def) => element.getAttribute(name) || def;
    const getBool = (name) => element.getAttribute(name) === "true";
    return {
        blockId: props?.blockId ?? getAttr("data-block-id", ""),
        searchRootId: props?.searchRootId ?? getAttr("data-search-root", ""),
        filterFeatured: props?.filterFeatured ?? getBool("data-filter-featured"),
        pageSize: props?.pageSize ?? parseInt(getAttr("data-page-size", "9"), 10),
        allText: props?.allText ?? getAttr("data-all-text", ""),
        viewMode: props?.viewMode ??
            getAttr("data-view-mode", "Listing"),
        viewAllBase: props?.viewAllBase ?? getAttr("data-view-all-base", ""),
    };
};
export function ArticleFeaturedListing(element, props) {
    const config = getConfiguration(element, props);
    if (!config.searchRootId) {
        devLogger.log("[ArticleFeaturedListing] Missing searchRootId. Component disabled.");
        return () => { };
    }
    const filterButtons = element.querySelectorAll(".category-filter-btn");
    const dropdown = element.querySelector("#dropdown-category-filter");
    const articlesGrid = element.querySelector("#articlesGrid");
    const gridWrapper = element.querySelector(".articles-grid-wrapper");
    const carouselContainer = element.querySelector('.article-carousel-swiper[data-component="ArticleCarousel"]');
    const loadMoreBtn = element.querySelector(".load-more-btn");
    const viewAllBtn = element.querySelector(".view-all-cta");
    const isFeaturedView = config.viewMode === "Featured";
    const isListingView = config.viewMode === "Listing";
    const state = {
        currentCategory: "",
        currentPage: parseInt(gridWrapper?.dataset.currentPage || "1", 10),
        isLoading: false,
    };
    const urlParams = new URLSearchParams(window.location.search);
    const categoryFromUrl = urlParams.get("category") || "";
    if (categoryFromUrl) {
        state.currentCategory = categoryFromUrl;
        updateActiveFilterButton(state.currentCategory);
        updateDropdownDisplay(state.currentCategory);
    }
    const cleanupFunctions = [];
    // Listen to dropdown selection events
    const handleDropdownSelect = (e) => {
        const customEvent = e;
        if (state.isLoading)
            return;
        const category = customEvent.detail?.value || "";
        if (category === state.currentCategory)
            return;
        state.currentCategory = category;
        state.currentPage = 1;
        updateActiveFilterButton(category);
        updateDropdownDisplay(category);
        loadArticles(category, state.currentPage, false);
        if (isFeaturedView && viewAllBtn) {
            updateViewAllLink(category);
        }
        if (isListingView) {
            updateBrowserUrl(category, state.currentPage);
        }
    };
    if (dropdown) {
        dropdown.addEventListener("dropdown:select", handleDropdownSelect);
        cleanupFunctions.push(() => {
            dropdown.removeEventListener("dropdown:select", handleDropdownSelect);
        });
    }
    filterButtons.forEach((btn) => {
        const handleFilterClick = () => {
            if (state.isLoading)
                return;
            const category = btn.dataset.category || "";
            if (category === state.currentCategory)
                return;
            state.currentCategory = category;
            state.currentPage = 1;
            updateActiveFilterButton(category);
            updateDropdownDisplay(category);
            loadArticles(category, state.currentPage, false);
            if (isFeaturedView && viewAllBtn) {
                updateViewAllLink(category);
            }
            if (isListingView) {
                updateBrowserUrl(category, state.currentPage);
            }
        };
        btn.addEventListener("click", handleFilterClick);
        cleanupFunctions.push(() => {
            btn.removeEventListener("click", handleFilterClick);
        });
    });
    if (loadMoreBtn) {
        const handleLoadMore = () => {
            if (state.isLoading)
                return;
            const nextPage = parseInt(loadMoreBtn.dataset.nextPage || "2", 10) || state.currentPage + 1;
            loadArticles(state.currentCategory, nextPage, true);
        };
        loadMoreBtn.addEventListener("click", handleLoadMore);
        cleanupFunctions.push(() => {
            loadMoreBtn.removeEventListener("click", handleLoadMore);
        });
    }
    const handlePopState = (event) => {
        if (event.state && isListingView) {
            state.currentCategory = event.state.category || "";
            state.currentPage = event.state.page || 1;
            updateActiveFilterButton(state.currentCategory);
            updateDropdownDisplay(state.currentCategory);
            loadArticles(state.currentCategory, state.currentPage, false);
        }
    };
    window.addEventListener("popstate", handlePopState);
    cleanupFunctions.push(() => {
        window.removeEventListener("popstate", handlePopState);
    });
    function updateActiveFilterButton(category) {
        filterButtons.forEach((btn) => {
            const btnCategory = btn.dataset.category || "";
            const isActive = btnCategory.toLowerCase() === category.toLowerCase();
            if (isActive) {
                btn.classList.add("bg-primary", "text-white");
                btn.setAttribute("aria-pressed", "true");
            }
            else {
                btn.classList.remove("bg-primary", "text-white");
                btn.setAttribute("aria-pressed", "false");
            }
        });
    }
    function updateDropdownDisplay(category) {
        if (!dropdown)
            return;
        // Dispatch update event to dropdown component
        const updateEvent = new CustomEvent("dropdown:update", {
            detail: { value: category },
            bubbles: true,
            cancelable: true,
        });
        dropdown.dispatchEvent(updateEvent);
    }
    function updateViewAllLink(category) {
        if (!viewAllBtn || !config.viewAllBase)
            return;
        if (!category) {
            viewAllBtn.href = config.viewAllBase;
            return;
        }
        try {
            const baseUrl = new URL(config.viewAllBase, window.location.origin);
            baseUrl.searchParams.set("category", category.toLowerCase());
            viewAllBtn.href = baseUrl.pathname + baseUrl.search + baseUrl.hash;
        }
        catch {
            const url = config.viewAllBase +
                (config.viewAllBase.includes("?") ? "&" : "?") +
                "category=" +
                encodeURIComponent(category);
            viewAllBtn.href = url;
        }
    }
    function updateBrowserUrl(category, page) {
        const params = new URLSearchParams();
        if (category) {
            params.set("category", category.toLowerCase());
        }
        if (page > 1) {
            params.set("page", page.toString());
        }
        const newUrl = params.toString()
            ? window.location.pathname + "?" + params.toString()
            : window.location.pathname;
        history.pushState({ category, page }, "", newUrl);
    }
    async function loadArticles(category, page, isLoadMore) {
        if (state.isLoading || !articlesGrid)
            return;
        state.isLoading = true;
        if (isLoadMore) {
            setLoadMoreLoading(true);
        }
        else {
            showLoadingSkeleton();
        }
        // Get language from data-lang attribute on <html> element
        const language = document.documentElement.getAttribute("data-lang") || "";
        const apiParams = new URLSearchParams({
            searchRootId: config.searchRootId,
            filterByFeatured: config.filterFeatured.toString(),
            page: page.toString(),
            pageSize: config.pageSize.toString(),
        });
        if (category && category !== config.allText) {
            apiParams.set("category", category);
        }
        if (language) {
            apiParams.set("language", language);
        }
        const apiUrl = `/api/articlelisting/filter?${apiParams.toString()}`;
        try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();
            if (isLoadMore) {
                articlesGrid.insertAdjacentHTML("beforeend", data.html);
            }
            else {
                articlesGrid.innerHTML = data.html;
            }
            state.currentPage = data.currentPage;
            if (loadMoreBtn) {
                if (data.hasMore) {
                    loadMoreBtn.style.display = "inline-flex";
                    loadMoreBtn.dataset.nextPage = (data.currentPage + 1).toString();
                }
                else {
                    loadMoreBtn.style.display = "none";
                }
            }
            if (gridWrapper) {
                gridWrapper.dataset.currentPage = data.currentPage.toString();
            }
            LoadingImageComponent(element);
            reinitializeCarousel();
        }
        catch (error) {
            devLogger.error("Error loading articles:", error);
            if (!isLoadMore) {
                articlesGrid.innerHTML =
                    '<div class="col-span-full text-center py-8 text-gray-500">Error loading articles. Please try again.</div>';
            }
        }
        finally {
            state.isLoading = false;
            if (isLoadMore) {
                setLoadMoreLoading(false);
            }
        }
    }
    function showLoadingSkeleton() {
        renderArticleSkeleton(articlesGrid, {
            count: config.pageSize,
            wrapperClass: "swiper-slide",
        });
    }
    function setLoadMoreLoading(loading) {
        if (!loadMoreBtn)
            return;
        const btnText = loadMoreBtn.querySelector(".btn-text");
        const btnLoading = loadMoreBtn.querySelector(".btn-loading");
        if (loading) {
            loadMoreBtn.disabled = true;
            if (btnText)
                btnText.classList.add("hidden");
            if (btnLoading)
                btnLoading.classList.remove("hidden");
        }
        else {
            loadMoreBtn.disabled = false;
            if (btnText)
                btnText.classList.remove("hidden");
            if (btnLoading)
                btnLoading.classList.add("hidden");
        }
    }
    function reinitializeCarousel() {
        if (!carouselContainer)
            return;
        setTimeout(() => {
            const reinitEvent = new CustomEvent("carousel:reinit", {
                bubbles: true,
                cancelable: true,
            });
            carouselContainer.dispatchEvent(reinitEvent);
        }, 100);
    }
    return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
    };
}
